import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import PageLayout from "components/layout/page-layout";
import { useSharedState } from "state";
import Table, { tableHeader } from "components/table";
import { oneLine } from "components/table/table-parts";
import ActionButton from "components/table/action-btn";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import DangerModal from "components/modal/danger";
import { ReportTemplate } from "types/budget";
import { deleteReportTemplate } from "api/budget_request";

export default function Constructor() {
  const { reportTemplates, getReportTemplatesData, getBudgetColumnsData, getBudgetForecastsData, notify } =
    useSharedState();

  const navigate = useNavigate();

  const [templateToDelete, setTemplateToDelete] = useState<ReportTemplate | undefined>(undefined);
  const [isFormLoading, setFormLoading] = useState<boolean>(false);

  const deleteTemplateDanger = useMemo(() => {
    return `Вы действительно хотите удалить шаблон "${templateToDelete?.template_name}"?`;
  }, [templateToDelete]);

  useEffect(() => {
    getBudgetColumnsData();
    getBudgetForecastsData();
    getReportTemplatesData();
  }, []);

  const handleTemplateDelete = async () => {
    try {
      setFormLoading(true);
      await deleteReportTemplate(templateToDelete?.budget_report_template_id!);
      notify("Шаблон удалён", "success");

      await getReportTemplatesData();
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", "Ошибка при удалении шаблона");
    } finally {
      setFormLoading(false);
    }
  };

  const constructorTableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: false },
    { header_text: "Наименование", narrow: false, wrap: false },
    { header_text: "Структура", narrow: false, wrap: false },
    { header_text: "Статус", narrow: false, wrap: false },
    { header_text: "Действия", narrow: false, wrap: false },
  ];

  const constructorList = (
    <>
      {reportTemplates.map((template, index) => (
        <tr key={index}>
          <td>{oneLine((index + 1).toString())}</td>
          <td>{oneLine(template.template_name || "-")}</td>
          <td>{oneLine(template.json_description.structure === "hierarchical" ? "Иерархичная" : "Табличная")}</td>
          <td>{oneLine(template.status ? "Активный" : "Неактивный", template.status ? "success" : "danger")}</td>
          <td>
            <div className="btn-list flex-nowrap">
              <ActionButton
                icon={<IconPencil className="icon" />}
                title="Изменить"
                action={() => navigate(`${template.budget_report_template_id}`)}
                disabled={isFormLoading}
                color={"info"}
                modal={false}
              />
              <ActionButton
                icon={<IconTrash className="icon" />}
                title="Удалить"
                action={() => setTemplateToDelete(template)}
                disabled={isFormLoading}
                color={"danger"}
                modal={true}
                modal_id="#modal-delete-report-template"
              />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const constructorTableComponent = Table(constructorTableHeaders, constructorList, false);

  return (
    <>
      <PageLayout
        children={constructorTableComponent}
        header={"Конструктор отчётов"}
        search={false}
        filters={false}
        buttonText={"Новый отчёт"}
        buttonAction={() => navigate("create")}
      />
      <DangerModal
        modal_id="modal-delete-report-template"
        dangerDescription={deleteTemplateDanger}
        cancel={() => setTemplateToDelete(undefined)}
        confirm={handleTemplateDelete}
      />
      <Outlet />
    </>
  );
}
